/**
 * 고정 중심점 수정 기능을 담당하는 클래스
 */
export class FixedCenterModificationHandler {
  constructor() {
    this.originalGeometries = new Map();
    this.originalCenters = new Map();
    this.originalRadii = new Map();
    this.originalAngles = new Map();
  }

  /**
   * 고정 중심점 수정 스타일 함수 생성
   * @param {Map} imagePolygonPairs - 이미지-폴리곤 쌍들
   * @param {Function} defaultStyle - 기본 스타일 함수
   * @returns {Function} 고정 중심점 수정 스타일 함수
   */
  createFixedCenterModificationStyleFunction(imagePolygonPairs, defaultStyle) {
    return (feature) => {
      // 기본 스타일 적용
      const baseStyle = defaultStyle ? defaultStyle(feature) : null;

      // 고정 중심점 수정용 스타일 추가
      const modificationStyle = this.createModificationStyle(feature);

      return [baseStyle, modificationStyle].filter(Boolean);
    };
  }

  /**
   * 수정 스타일 생성
   * @param {ol/Feature} feature - 폴리곤 피처
   * @returns {ol/style/Style|null} 수정 스타일
   */
  createModificationStyle(feature) {
    if (!feature) return null;

    // 수정 중인 상태일 때만 특별한 스타일 적용
    const properties = feature.getProperties();
    if (properties.isModifying) {
      return this.createModifyingStyle();
    }

    return null;
  }

  /**
   * 수정 중인 스타일 생성
   * @returns {ol/style/Style} 수정 중인 스타일
   */
  createModifyingStyle() {
    return new (require('ol/style/Style').default)({
      stroke: new (require('ol/style/Stroke').default)({
        color: '#FF5722',
        width: 3,
        lineDash: [5, 5]
      }),
      fill: new (require('ol/style/Fill').default)({
        color: 'rgba(255, 87, 34, 0.1)'
      })
    });
  }

  /**
   * 폴리곤 수정 시작 시 원본 정보 저장
   * @param {ol/Feature} feature - 폴리곤 피처
   */
  storeOriginalGeometry(feature) {
    if (!feature) return;

    const featureId = feature.getId();
    const geometry = feature.getGeometry();

    if (!geometry) return;

    // 원본 지오메트리 저장
    this.originalGeometries.set(featureId, geometry.clone());

    // 중심점과 반지름 계산
    const centerAndRadius = this.calculateCenterAndRadius(geometry);
    this.originalCenters.set(featureId, centerAndRadius.center);
    this.originalRadii.set(featureId, centerAndRadius.radius);
    this.originalAngles.set(featureId, centerAndRadius.angles);

    console.log(`FixedCenterModificationHandler: 원본 정보 저장 - ID: ${featureId}`, {
      center: centerAndRadius.center,
      radius: centerAndRadius.radius,
      angles: centerAndRadius.angles
    });
  }

  /**
   * 중심점과 반지름 계산
   * @param {ol/geom/Geometry} geometry - 폴리곤 지오메트리
   * @returns {Object} 중심점, 반지름, 각도 정보
   */
  calculateCenterAndRadius(geometry) {
    const extent = geometry.getExtent();
    const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

    const coordinates = geometry.getCoordinates()[0];
    const radius = [];
    const angles = [];

    coordinates.forEach((coord, index) => {
      const dx = coord[0] - center[0];
      const dy = coord[1] - center[1];
      radius.push(Math.sqrt(dx * dx + dy * dy));
      angles.push(Math.atan2(dy, dx));
    });

    return { center, radius, angles };
  }

  /**
   * 고정 중심점 수정 적용
   * @param {ol/Feature} feature - 폴리곤 피처
   * @param {Array} newCoordinates - 새로운 좌표
   */
  applyFixedCenterModification(feature, newCoordinates) {
    if (!feature) return;

    const featureId = feature.getId();
    const originalCenter = this.originalCenters.get(featureId);
    const originalRadius = this.originalRadii.get(featureId);
    const originalAngles = this.originalAngles.get(featureId);

    if (!originalCenter || !originalRadius || !originalAngles) {
      console.warn('FixedCenterModificationHandler: 원본 정보가 없습니다');
      return;
    }

    // 새로운 중심점과 반지름 계산
    const newCenterAndRadius = this.calculateCenterAndRadius(
      new (require('ol/geom/Polygon').default)([newCoordinates])
    );

    // 스케일과 회전 계산
    const scale = this.calculateScale(originalRadius, newCenterAndRadius.radius);
    const rotation = this.calculateRotation(originalAngles, newCenterAndRadius.angles);

    // 원본 지오메트리 가져오기
    const originalGeometry = this.originalGeometries.get(featureId);
    if (!originalGeometry) return;

    // 변형 적용
    const modifiedGeometry = originalGeometry.clone();
    modifiedGeometry.scale(scale, originalCenter);
    modifiedGeometry.rotate(rotation, originalCenter);

    // 피처에 적용
    feature.setGeometry(modifiedGeometry);

    console.log(`FixedCenterModificationHandler: 고정 중심점 수정 적용 - ID: ${featureId}`, {
      scale,
      rotation: rotation * 180 / Math.PI,
      center: originalCenter
    });
  }

  /**
   * 스케일 계산
   * @param {Array} originalRadius - 원본 반지름 배열
   * @param {Array} newRadius - 새로운 반지름 배열
   * @returns {number} 스케일 비율
   */
  calculateScale(originalRadius, newRadius) {
    if (!originalRadius || !newRadius || originalRadius.length !== newRadius.length) {
      return 1;
    }

    const ratios = originalRadius.map((orig, index) => {
      const newRad = newRadius[index];
      return newRad > 0 ? orig / newRad : 1;
    });

    // 평균 스케일 계산
    const averageScale = ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length;
    return averageScale;
  }

  /**
   * 회전 각도 계산
   * @param {Array} originalAngles - 원본 각도 배열
   * @param {Array} newAngles - 새로운 각도 배열
   * @returns {number} 회전 각도 (라디안)
   */
  calculateRotation(originalAngles, newAngles) {
    if (!originalAngles || !newAngles || originalAngles.length !== newAngles.length) {
      return 0;
    }

    const angleDiffs = originalAngles.map((orig, index) => {
      const newAngle = newAngles[index];
      let diff = orig - newAngle;

      // 각도 정규화 (-π ~ π)
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;

      return diff;
    });

    // 평균 회전 각도 계산
    const averageRotation = angleDiffs.reduce((sum, diff) => sum + diff, 0) / angleDiffs.length;
    return averageRotation;
  }

  /**
   * 수정 완료 시 정리
   * @param {ol/Feature} feature - 폴리곤 피처
   */
  cleanupModification(feature) {
    if (!feature) return;

    const featureId = feature.getId();

    // 저장된 원본 정보 제거
    this.originalGeometries.delete(featureId);
    this.originalCenters.delete(featureId);
    this.originalRadii.delete(featureId);
    this.originalAngles.delete(featureId);

    console.log(`FixedCenterModificationHandler: 수정 완료 정리 - ID: ${featureId}`);
  }

  /**
   * 모든 수정 정보 정리
   */
  cleanupAll() {
    this.originalGeometries.clear();
    this.originalCenters.clear();
    this.originalRadii.clear();
    this.originalAngles.clear();

    console.log('FixedCenterModificationHandler: 모든 수정 정보 정리 완료');
  }
}
